import React, { useEffect, useRef } from "react";
import classNames from "classnames";
import { useNavigate } from "react-router";
import { usePlatform } from "../../hooks/usePlatform";
import backStyles from "../web-back-button/style.module.css";
import styles from "./style.module.css";

const SPACER_INITIAL_MOBILE = 20;
const SPACER_INITIAL_DESKTOP = 150;
const SPACER_MIN = 0;

export type DocumentPageVariant = "document" | "scrollable";

interface DocumentPageProps {
  className?: string;
  /** scrollable — инструкции (чёрный фон), document — юр. тексты */
  variant?: DocumentPageVariant;
  children: React.ReactNode;
}

/**
 * Скролл-обёртка: верхний «воздух» (≥768px) схлопывается при скролле,
 * «Назад» в потоке контента (уезжает вверх вместе с текстом).
 */
function DocumentPage({
  className,
  variant = "document",
  children,
}: DocumentPageProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { isTelegram } = usePlatform();

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const mq = window.matchMedia("(min-width: 768px)");
    const getInitial = () =>
      mq.matches ? SPACER_INITIAL_DESKTOP : SPACER_INITIAL_MOBILE;

    const updateSpacer = () => {
      if (!mq.matches) {
        el.style.setProperty("--document-spacer", "0px");
        return;
      }
      const initial = getInitial();
      const next = Math.max(SPACER_MIN, initial - el.scrollTop);
      el.style.setProperty("--document-spacer", `${next}px`);
    };

    updateSpacer();
    el.addEventListener("scroll", updateSpacer, { passive: true });
    mq.addEventListener("change", updateSpacer);
    return () => {
      el.removeEventListener("scroll", updateSpacer);
      mq.removeEventListener("change", updateSpacer);
    };
  }, []);

  return (
    <div
      ref={scrollRef}
      className={classNames(
        variant === "scrollable" ? "scrollable-page" : "document",
        styles.documentPage,
        variant === "scrollable" && styles.scrollableSurface,
        className
      )}
    >
      <div className={styles.spacer} aria-hidden />
      <div
        className={classNames(
          styles.body,
          isTelegram && styles.bodyTelegram
        )}
      >
        {!isTelegram && (
          <button
            type="button"
            className={classNames(backStyles.backButton, styles.backInFlow)}
            onClick={() => navigate(-1)}
            aria-label="Назад"
          >
            <span className={backStyles.chevron} aria-hidden>
              &#8249;
            </span>
            <span>Назад</span>
          </button>
        )}
        {children}
      </div>
    </div>
  );
}

export default DocumentPage;
